import { Platform } from 'react-native';

type StabilityListener = () => void;

interface StabilityConditions {
  authResolved: boolean;
  authModalClosed: boolean;
  gestureSystemReady: boolean;
  navigationStable: boolean;
  recoveryComplete: boolean;
  loginTransactionComplete: boolean;
}

const GESTURE_RECOVERY_DELAY_IOS = 700;
const GESTURE_RECOVERY_DELAY_ANDROID = 400;
const STABILITY_CHECK_INTERVAL = 50;
const MAX_STABILITY_WAIT = 5000;

class AppStabilityGuard {
  private static instance: AppStabilityGuard;

  private conditions: StabilityConditions = {
    authResolved: true,
    authModalClosed: true,
    gestureSystemReady: true,
    navigationStable: true,
    recoveryComplete: true,
    loginTransactionComplete: true,
  };

  private listeners: Set<StabilityListener> = new Set();
  private gestureRecoveryTimer: NodeJS.Timeout | null = null;
  private lastModalCloseTimestamp: number = 0;
  private lastAuthChangeTimestamp: number = 0;

  private constructor() {
    console.log('[APP-STABILITY] Guard initialized');
  }

  static getInstance(): AppStabilityGuard {
    if (!AppStabilityGuard.instance) {
      AppStabilityGuard.instance = new AppStabilityGuard();
    }
    return AppStabilityGuard.instance;
  }

  setAuthResolved(resolved: boolean): void {
    const changed = this.conditions.authResolved !== resolved;
    this.conditions.authResolved = resolved;

    if (changed) {
      this.lastAuthChangeTimestamp = Date.now();
      console.log(`[APP-STABILITY] authResolved: ${resolved}`);
      this.notifyIfStable();
    }
  }

  setAuthModalClosed(closed: boolean): void {
    const changed = this.conditions.authModalClosed !== closed;
    this.conditions.authModalClosed = closed;

    if (changed) {
      console.log(`[APP-STABILITY] authModalClosed: ${closed}`);

      if (closed) {
        this.lastModalCloseTimestamp = Date.now();
        this.startGestureRecoveryTimer();
      } else {
        this.conditions.gestureSystemReady = false;
        if (this.gestureRecoveryTimer) {
          clearTimeout(this.gestureRecoveryTimer);
          this.gestureRecoveryTimer = null;
        }
      }

      this.notifyIfStable();
    }
  }

  setNavigationStable(stable: boolean): void {
    const changed = this.conditions.navigationStable !== stable;
    this.conditions.navigationStable = stable;

    if (changed) {
      console.log(`[APP-STABILITY] navigationStable: ${stable}`);
      this.notifyIfStable();
    }
  }

  setRecoveryInProgress(inProgress: boolean): void {
    const recoveryComplete = !inProgress;
    const changed = this.conditions.recoveryComplete !== recoveryComplete;
    this.conditions.recoveryComplete = recoveryComplete;

    if (changed) {
      console.log(`[APP-STABILITY] recoveryInProgress: ${inProgress} (recoveryComplete: ${recoveryComplete})`);
      this.notifyIfStable();
    }
  }

  isRecoveryInProgress(): boolean {
    return !this.conditions.recoveryComplete;
  }

  setLoginTransactionComplete(complete: boolean): void {
    const changed = this.conditions.loginTransactionComplete !== complete;
    this.conditions.loginTransactionComplete = complete;

    if (changed) {
      console.log(`[APP-STABILITY] loginTransactionComplete: ${complete}`);
      this.notifyIfStable();
    }
  }

  isLoginTransactionInProgress(): boolean {
    return !this.conditions.loginTransactionComplete;
  }

  private startGestureRecoveryTimer(): void {
    if (this.gestureRecoveryTimer) {
      clearTimeout(this.gestureRecoveryTimer);
    }

    const delay = Platform.OS === 'ios' ? GESTURE_RECOVERY_DELAY_IOS : GESTURE_RECOVERY_DELAY_ANDROID;

    console.log(`[APP-STABILITY] Starting gesture recovery timer: ${delay}ms`);

    this.gestureRecoveryTimer = setTimeout(() => {
      this.conditions.gestureSystemReady = true;
      console.log('[APP-STABILITY] gestureSystemReady: true (after delay)');
      this.gestureRecoveryTimer = null;
      this.notifyIfStable();
    }, delay);
  }

  isStable(): boolean {
    const stable = Object.values(this.conditions).every(v => v === true);
    return stable;
  }

  getConditions(): StabilityConditions {
    return { ...this.conditions };
  }

  getTimeSinceLastModalClose(): number {
    if (this.lastModalCloseTimestamp === 0) return Infinity;
    return Date.now() - this.lastModalCloseTimestamp;
  }

  getTimeSinceLastAuthChange(): number {
    if (this.lastAuthChangeTimestamp === 0) return Infinity;
    return Date.now() - this.lastAuthChangeTimestamp;
  }

  async waitForStability(timeoutMs: number = MAX_STABILITY_WAIT): Promise<boolean> {
    if (this.isStable()) {
      console.log('[APP-STABILITY] Already stable');
      return true;
    }

    console.log(`[APP-STABILITY] Waiting for stability (timeout: ${timeoutMs}ms)...`);
    const startTime = Date.now();

    return new Promise((resolve) => {
      const checkStability = () => {
        if (this.isStable()) {
          console.log(`[APP-STABILITY] Stability achieved after ${Date.now() - startTime}ms`);
          resolve(true);
          return;
        }

        if (Date.now() - startTime >= timeoutMs) {
          console.log(`[APP-STABILITY] Timeout waiting for stability. Conditions:`, this.conditions);
          resolve(false);
          return;
        }

        setTimeout(checkStability, STABILITY_CHECK_INTERVAL);
      };

      checkStability();
    });
  }

  addListener(listener: StabilityListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notifyIfStable(): void {
    if (this.isStable()) {
      console.log('[APP-STABILITY] All conditions met, notifying listeners');
      this.listeners.forEach(listener => {
        try {
          listener();
        } catch (e) {
          console.error('[APP-STABILITY] Listener error:', e);
        }
      });
    }
  }

  reset(): void {
    if (this.gestureRecoveryTimer) {
      clearTimeout(this.gestureRecoveryTimer);
      this.gestureRecoveryTimer = null;
    }

    this.conditions = {
      authResolved: true,
      authModalClosed: true,
      gestureSystemReady: true,
      navigationStable: true,
      recoveryComplete: true,
      loginTransactionComplete: true,
    };

    this.lastModalCloseTimestamp = 0;
    this.lastAuthChangeTimestamp = 0;

    console.log('[APP-STABILITY] Guard reset');
  }
}

export const appStabilityGuard = AppStabilityGuard.getInstance();
